import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, Row, UiButton, UiFormControl, UiQuestionnaireLayout, UiText, UiTextarea} from '~/components/elements';
import {
  BMI_27,
  BMI_30,
  NONE_OF_THE_ABOVE_VALUE,
  WEIGHT_LOSS_KEYS,
  YES_OPTION,
} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire18Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === WEIGHT_LOSS_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]?.data,
  );
  const currentBMIAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.BMI_CALCULATOR]?.data,
  );
  const currentObesityAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.OBESITY_RELATED_CONDITIONS]?.data,
  );

  const noneOptionObesitySymptoms = currentObesityAnswer?.selectedOptions?.includes(NONE_OF_THE_ABOVE_VALUE);

  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    medicationsTaking: currentAnswer?.medicationsTaking || '',
  });
  const onClickSubmit = () => {
    let data = inputs?.selectedOption === YES_OPTION ? inputs : {selectedOption: inputs?.selectedOption};
    dispatch(
      setQuestionnaireAnswer({
        key: WEIGHT_LOSS_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
        index: questionnaire?.index || '',
        data,
      }),
    );
    if (
      currentBMIAnswer?.bmi >= BMI_30 ||
      (currentBMIAnswer?.bmi >= BMI_27 && currentBMIAnswer?.bmi < BMI_30 && !noneOptionObesitySymptoms)
    ) {
      dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.METABOLIC_ASSESSMENT}));
      return;
    }

    if (
      currentBMIAnswer?.bmi < BMI_27 ||
      (currentBMIAnswer?.bmi >= BMI_27 && currentBMIAnswer?.bmi < BMI_30 && noneOptionObesitySymptoms)
    ) {
      dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.DOCTOR_ASSESSMENT}));
      return;
    }
  };

  const renderRelations = () => {
    const relations = questionnaire?.options?.find(relation => relation?.value === inputs?.selectedOption)?.relations;
    return (
      <>
        {relations?.map((relation, index) => (
          <Col key={index} w="100%">
            <UiText fontWeight={700} fontSize="16px" mt={10}>
              {relation?.questions?.default}
            </UiText>
            <UiFormControl id="medicationsTaking" isRequired>
              <UiTextarea
                mt={spacing.small}
                bg="white"
                borderRadius={spacing.tiny}
                placeholder="Provide as much detail as possible"
                defaultValue={inputs?.medicationsTaking}
                onChange={({target}) => setInputs({medicationsTaking: target.value})}
                minH="200px"
              />
            </UiFormControl>
          </Col>
        ))}
      </>
    );
  };
  const isDisable = !inputs?.selectedOption || (inputs?.selectedOption === YES_OPTION && !inputs?.medicationsTaking);

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisable}
    >
      <Col align="center" textAlign="center" h="100%">
        <UiText variant="mdqTitle" my={10}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row justifyContent="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({selectedOption: value?.value})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              mb={6}
              variant={inputs?.selectedOption === value.value ? 'solid' : 'toggle'}
              w="100%"
              mx={1}
            />
          ))}
        </Row>

        {inputs?.selectedOption !== YES_OPTION &&
          questionnaire?.descriptions?.default?.map(desc => (
            <UiText key={desc} color="liteOuter" fontSize="14px">
              {desc}
            </UiText>
          ))}
        {renderRelations()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire18Screen;
